import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { api } from '../lib/api';
import toast from 'react-hot-toast';
import {
  Robot,
  Plus,
  Pencil,
  Trash,
  Power,
  CaretRight,
  ArrowLeft,
  Check,
  Sparkle,
  Warning,
  Lightning,
  X
} from '@phosphor-icons/react';

const PLAN_LIMITS = {
  free: { agents: 1 },
  starter: { agents: 1 },
  pro: { agents: 3 },
  business: { agents: 999 },
};

// Agents List
export function AgentsList() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const { company } = useOutletContext();
  const navigate = useNavigate();

  const plan = company?.plan || 'free';
  const maxAgents = PLAN_LIMITS[plan]?.agents || 1;

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      const res = await api.getAgents();
      setAgents(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este agente?')) return;
    try {
      await api.deleteAgent(id);
      setAgents(agents.filter(a => a.id !== id));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleCreateClick = () => {
    if (agents.length >= maxAgents) {
      setShowLimitModal(true);
    } else {
      navigate('/dashboard/agents/new');
    }
  };

  const toggleAgent = async (agent) => {
    try {
      await api.updateAgent(agent.id, { is_active: !agent.is_active });
      setAgents(agents.map(a => 
        a.id === agent.id ? { ...a, is_active: !a.is_active } : a
      ));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#0047FF] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-black text-2xl lg:text-3xl tracking-tighter text-[#09090B]">
            Mis Agentes
          </h1>
          <p className="text-[#52525B]">Gestiona tus agentes IA</p>
        </div>
        <button
          onClick={handleCreateClick}
          data-testid="create-agent-btn"
          className="flex items-center gap-2 bg-[#0047FF] text-white rounded-xl px-4 py-2 font-medium hover:bg-[#0035CC] transition-colors"
        >
          <Plus size={20} />
          Crear agente
        </button>
      </div>

      {agents.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl shadow-sm border border-[#E4E4E7]">
          <Robot size={48} className="mx-auto text-[#A1A1AA] mb-4" />
          <h3 className="font-bold text-lg text-[#09090B] mb-2">No tienes agentes</h3>
          <p className="text-[#52525B] mb-6">Crea tu primer agente IA para comenzar</p>
          <button
            onClick={handleCreateClick}
            className="inline-flex items-center gap-2 bg-[#0047FF] text-white rounded-xl px-6 py-3 font-medium"
          >
            <Sparkle size={20} />
            Crear con wizard IA
          </button>
        </div>
      ) : (
        <div className="border border-[#E4E4E7] bg-white rounded-2xl shadow-sm overflow-hidden">
          {agents.map((agent, index) => (
            <div 
              key={agent.id}
              data-testid={`agent-${agent.id}`}
              className={`flex items-center gap-4 p-4 ${index !== agents.length - 1 ? 'border-b border-[#E4E4E7]' : ''}`}
            >
              <div className={`w-12 h-12 flex items-center justify-center ${agent.is_active ? 'bg-[#0047FF]' : 'bg-[#A1A1AA]'}`}>
                <Robot size={24} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-[#09090B]">{agent.name}</p>
                <p className="text-sm text-[#52525B] truncate">{agent.description}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 text-xs font-medium ${agent.is_active ? 'bg-green-100 text-green-700' : 'bg-[#E4E4E7] text-[#52525B]'}`}>
                  {agent.is_active ? 'Activo' : 'Inactivo'}
                </span>
                <button
                  onClick={() => toggleAgent(agent)}
                  className="p-2 hover:bg-[#F4F4F5] transition-colors"
                  title={agent.is_active ? 'Desactivar' : 'Activar'}
                >
                  <Power size={18} className={agent.is_active ? 'text-green-600' : 'text-[#A1A1AA]'} />
                </button>
                <button
                  onClick={() => navigate(`/dashboard/agents/${agent.id}`)}
                  className="p-2 hover:bg-[#F4F4F5] transition-colors"
                  title="Editar"
                >
                  <Pencil size={18} className="text-[#52525B]" />
                </button>
                <button
                  onClick={() => handleDelete(agent.id)}
                  className="p-2 hover:bg-red-50 transition-colors"
                  title="Eliminar"
                >
                  <Trash size={18} className="text-red-500" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Limit Reached Modal */}
      {showLimitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-[#E4E4E7] flex items-center justify-between">
              <h3 className="font-bold text-lg text-[#09090B]">Límite de agentes alcanzado</h3>
              <button onClick={() => setShowLimitModal(false)} className="text-[#A1A1AA] hover:text-[#09090B]">
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-yellow-100 flex items-center justify-center">
                  <Warning size={24} className="text-yellow-600" />
                </div>
                <div>
                  <p className="font-medium text-[#09090B]">
                    Tu plan <span className="capitalize">{plan}</span> permite máximo {maxAgents} {maxAgents === 1 ? 'agente' : 'agentes'}
                  </p>
                  <p className="text-sm text-[#52525B]">
                    Actualmente tienes {agents.length} {agents.length === 1 ? 'agente creado' : 'agentes creados'}
                  </p>
                </div>
              </div>
              <p className="text-[#52525B] text-sm mb-4">
                Para crear más agentes, actualiza tu suscripción a un plan superior.
              </p>
            </div>
            <div className="p-6 border-t border-[#E4E4E7] flex gap-3">
              <button
                onClick={() => setShowLimitModal(false)}
                className="flex-1 py-2 border border-[#E4E4E7] rounded-xl font-medium hover:bg-[#F4F4F5]"
              >
                Cerrar
              </button>
              <button
                onClick={() => navigate('/dashboard/billing')}
                className="flex-1 py-2 bg-[#0047FF] text-white rounded-xl font-medium hover:bg-[#0035CC] flex items-center justify-center gap-2"
              >
                <Lightning size={16} />
                Actualizar plan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Create Agent Wizard
export function CreateAgent() {
  const [step, setStep] = useState(0);
  const [wizardData, setWizardData] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [agentData, setAgentData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    startWizard();
  }, []);

  const startWizard = async () => {
    setLoading(true);
    try {
      const res = await api.startWizard();
      setCurrentQuestion(res.data);
      setStep(1);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const [generating, setGenerating] = useState(false);

  const handleNext = async () => {
    // Allow empty input for optional fields
    const isOptional = currentQuestion?.options === undefined && step === 2; // website step is optional
    if (!userInput.trim() && !isOptional) return;

    setLoading(true);

    try {
      const res = await api.wizardStep(step, userInput.trim() || '');

      if (res.data.is_complete) {
        setGenerating(true); // Show generating screen
        setLoading(false);
        setGeneratedPrompt(res.data.generated_prompt);
        setAgentData(res.data.agent_data);
        setGenerating(false);
        setStep(-1); // Review step
      } else {
        setCurrentQuestion(res.data);
        setStep(res.data.step);
        setUserInput('');
      }
    } catch (error) {
      console.error('Error:', error);
      setGenerating(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!agentData) return;
    setLoading(true);
    
    try {
      await api.createAgent(agentData);
      navigate('/dashboard/agents');
    } catch (error) {
      console.error('Error:', error);
      alert(error.response?.data?.detail || 'Error al crear el agente');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto">
      <button
        onClick={() => navigate('/dashboard/agents')}
        className="flex items-center gap-2 text-[#52525B] hover:text-[#09090B] mb-8"
      >
        <ArrowLeft size={20} />
        Volver a agentes
      </button>

      <div className="bg-white rounded-2xl shadow-sm border border-[#E4E4E7]">
        <div className="p-6 border-b border-[#E4E4E7]">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#0047FF] flex items-center justify-center">
              <Sparkle size={24} className="text-white" />
            </div>
            <div>
              <h1 className="font-black text-xl tracking-tighter text-[#09090B]">
                {step === -1 ? 'Revisa tu agente' : 'Wizard de creación'}
              </h1>
              <p className="text-sm text-[#52525B]">
                {step === -1 ? 'Verifica y ajusta antes de crear' : `Paso ${step} de 9`}
              </p>
            </div>
          </div>
          
          {step > 0 && (
            <div className="mt-4 h-1 bg-[#E4E4E7]">
              <div 
                className="h-full bg-[#0047FF] transition-all"
                style={{ width: `${(step / 9) * 100}%` }}
              />
            </div>
          )}
        </div>

        <div className="p-6">
          {generating ? (
            <div className="flex flex-col items-center justify-center py-16 space-y-6">
              <div className="relative">
                <div className="w-16 h-16 bg-[#0047FF]/10 flex items-center justify-center rounded-full">
                  <Sparkle size={32} className="text-[#0047FF] animate-pulse" />
                </div>
                <div className="absolute inset-0 w-16 h-16 border-2 border-[#0047FF] border-t-transparent rounded-full animate-spin" />
              </div>
              <div className="text-center">
                <h3 className="font-bold text-lg text-[#09090B] mb-2">Generando System Prompt con IA</h3>
                <p className="text-sm text-[#52525B] max-w-md">
                  Estamos analizando la información de tu negocio para crear un prompt personalizado y optimizado para tu agente...
                </p>
              </div>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-[#0047FF] border-t-transparent rounded-full" />
            </div>
          ) : step === -1 ? (
            // Review Step
            <div className="space-y-6">
              <div>
                <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
                  Nombre del agente
                </label>
                <input
                  type="text"
                  value={agentData?.name || ''}
                  onChange={(e) => setAgentData({ ...agentData, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:border-2 focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
                  Prompt del sistema
                </label>
                <textarea
                  value={generatedPrompt}
                  onChange={(e) => {
                    setGeneratedPrompt(e.target.value);
                    setAgentData({ ...agentData, system_prompt: e.target.value });
                  }}
                  rows={12}
                  className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:border-2 focus:outline-none font-mono text-sm"
                />
              </div>

              <button
                onClick={handleCreate}
                disabled={loading}
                data-testid="confirm-create-agent"
                className="w-full bg-[#0047FF] text-white rounded-xl py-3 font-medium hover:bg-[#0035CC] transition-colors flex items-center justify-center gap-2"
              >
                <Check size={20} />
                Crear agente
              </button>
            </div>
          ) : currentQuestion ? (
            // Question Step
            <div className="space-y-6">
              <div className="bg-[#F4F4F5] p-4 border-l-2 border-[#0047FF]">
                <p className="text-[#09090B]">{currentQuestion.question}</p>
              </div>

              {currentQuestion.options ? (
                <div className="space-y-2">
                  {currentQuestion.options.map((option) => (
                    <button
                      key={option}
                      onClick={() => setUserInput(option)}
                      className={`w-full text-left px-4 py-3 border transition-colors ${
                        userInput === option 
                          ? 'border-[#0047FF] bg-[#0047FF]/5' 
                          : 'border-[#E4E4E7] hover:border-[#A1A1AA]'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              ) : (
                <textarea
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  placeholder="Escribe tu respuesta..."
                  rows={4}
                  data-testid="wizard-input"
                  className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:border-2 focus:outline-none"
                />
              )}

              <button
                onClick={handleNext}
                disabled={(!userInput.trim() && !currentQuestion?.optional) || loading}
                data-testid="wizard-next"
                className="w-full bg-[#0047FF] text-white rounded-xl py-3 font-medium hover:bg-[#0035CC] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {currentQuestion?.optional && !userInput.trim() ? 'Omitir' : 'Continuar'}
                <CaretRight size={20} />
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// Edit Agent
export function EditAgent() {
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const agentId = window.location.pathname.split('/').pop();

  useEffect(() => {
    fetchAgent();
  }, [agentId]);

  const fetchAgent = async () => {
    try {
      const res = await api.getAgent(agentId);
      setAgent(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateAgent(agentId, {
        name: agent.name,
        description: agent.description,
        system_prompt: agent.system_prompt,
        tone: agent.tone,
        followups_enabled: agent.followups_enabled ?? true,
        followup_1_minutes: Number(agent.followup_1_minutes) || 30,
        followup_2_hours: Number(agent.followup_2_hours) || 4
      });
      navigate('/dashboard/agents');
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#0047FF] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!agent) {
    return <div className="p-8 text-center">Agente no encontrado</div>;
  }

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto">
      <button
        onClick={() => navigate('/dashboard/agents')}
        className="flex items-center gap-2 text-[#52525B] hover:text-[#09090B] mb-8"
      >
        <ArrowLeft size={20} />
        Volver a agentes
      </button>

      <div className="bg-white rounded-2xl shadow-sm border border-[#E4E4E7]">
        <div className="p-6 border-b border-[#E4E4E7]">
          <h1 className="font-black text-xl tracking-tighter text-[#09090B]">
            Editar Agente
          </h1>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
              Nombre
            </label>
            <input
              type="text"
              value={agent.name}
              onChange={(e) => setAgent({ ...agent, name: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:border-2 focus:outline-none"
            />
          </div>

          <div>
            <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
              Descripción
            </label>
            <input
              type="text"
              value={agent.description}
              onChange={(e) => setAgent({ ...agent, description: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:border-2 focus:outline-none"
            />
          </div>

          <div>
            <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
              Tono
            </label>
            <select
              value={agent.tone}
              onChange={(e) => setAgent({ ...agent, tone: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:border-2 focus:outline-none"
            >
              <option value="formal">Formal</option>
              <option value="casual">Casual</option>
              <option value="friendly">Amigable</option>
              <option value="professional">Profesional</option>
            </select>
          </div>

          <div>
            <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
              Prompt del sistema
            </label>
            <textarea
              value={agent.system_prompt}
              onChange={(e) => setAgent({ ...agent, system_prompt: e.target.value })}
              rows={12}
              className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:border-2 focus:outline-none font-mono text-sm"
            />
          </div>

          {/* Automated follow-ups */}
          <div className="border border-[#E4E4E7] rounded-xl p-5">
            <div className="flex items-start justify-between gap-4 mb-1">
              <div>
                <p className="font-bold text-[#09090B]">Seguimientos automáticos</p>
                <p className="text-sm text-[#52525B]">
                  Si el cliente deja de responder, el agente le envía hasta 2 mensajes de seguimiento
                  (solo en horario de oficina: L-V 9:00-19:00, Sáb 9:00-14:00).
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={agent.followups_enabled ?? true}
                onClick={() => setAgent({ ...agent, followups_enabled: !(agent.followups_enabled ?? true) })}
                data-testid="followups-toggle"
                className={`relative w-12 h-7 rounded-full transition-colors flex-shrink-0 ${
                  (agent.followups_enabled ?? true) ? 'bg-[#0047FF]' : 'bg-[#E4E4E7]'
                }`}
              >
                <span
                  className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-all ${
                    (agent.followups_enabled ?? true) ? 'left-6' : 'left-1'
                  }`}
                />
              </button>
            </div>
            {(agent.followups_enabled ?? true) && (
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
                    1er seguimiento (minutos)
                  </label>
                  <input
                    type="number"
                    min={5}
                    max={1440}
                    value={agent.followup_1_minutes ?? 30}
                    onChange={(e) => setAgent({ ...agent, followup_1_minutes: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none"
                  />
                  <p className="text-xs text-[#A1A1AA] mt-1">Minutos sin respuesta para el primer recordatorio</p>
                </div>
                <div>
                  <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
                    2do seguimiento (horas)
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={agent.followup_2_hours ?? 4}
                    onChange={(e) => setAgent({ ...agent, followup_2_hours: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none"
                  />
                  <p className="text-xs text-[#A1A1AA] mt-1">Horas después del primero; ofrece pasar con un asesor</p>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-agent"
            className="w-full bg-[#0047FF] text-white rounded-xl py-3 font-medium hover:bg-[#0035CC] transition-colors disabled:opacity-50"
          >
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
}
